import * as React from 'react';

import { cn } from '../../lib/utils';

/**
 * One part of a long form on a card of its own: "Your details", "Payment".
 * Splits a form into steps a visitor can see the end of, without splitting it
 * into pages. Adapted from ISSA's `SectionCard`.
 *
 *   <Form form={form} onSubmit={…} className="flex flex-col gap-card">
 *     <FormSection title="Your details" description="So we know who is coming.">
 *       <Field name="fullName" label="Full name" required><Input /></Field>
 *       <Field name="email" label="Email" required><Input type="email" /></Field>
 *     </FormSection>
 *     <FormSection title="Payment">…</FormSection>
 *   </Form>
 *
 * Every form spaces its cards with `gap-card`, so the gap is the same on every
 * page and steps down on a phone with the token (24px, 16px below `sm`). The
 * alerts and buttons after the last card sit in the same column at that gap.
 *
 * A titled section is a `<section>` named by its title, so a screen reader
 * can jump between them. Without a title it is a plain card, for a form
 * short enough to need only the one.
 */
export function FormSection({
  title,
  description,
  aside,
  className,
  children,
}: {
  title?: string;
  /** One line under the title: what this part asks for, or why. */
  description?: React.ReactNode;
  /** Beside the title, at the far end of its row: a step count, an edit link. */
  aside?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  const titleId = React.useId();
  const Tag = title ? 'section' : 'div';

  return (
    <Tag
      aria-labelledby={title ? titleId : undefined}
      className={cn(
        'flex flex-col gap-5 rounded-card bg-surface p-card text-ink shadow-card',
        className,
      )}
    >
      {(title || description || aside) && (
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            {/* Medium, not semibold: the card already sets it apart. */}
            {title && (
              <h2 id={titleId} className="text-lg font-medium">
                {title}
              </h2>
            )}
            {description && <p className="text-sm text-ink-muted">{description}</p>}
          </div>
          {aside && <div className="shrink-0">{aside}</div>}
        </div>
      )}
      {children}
    </Tag>
  );
}
